import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Label, ListBox, Pagination, Select, Surface } from "@heroui/react";
import { LoadingState } from "../components/LoadingState";
import { PortalFormSaveBar } from "../components/PortalFormSaveBar";
import { WiderrufFormPreview } from "../components/WiderrufFormPreview";
import { EXTENSION_TEXT_KEYS } from "../constants/extensionTextKeys";
import { PortalTextField } from "../components/form/PortalTextField";
import { useLocale } from "../context/LocaleContext";
import { apiFetch } from "../lib/apiClient";
import { isFormDirty } from "../lib/formDirty";
import { showErrorToast, showSuccessToast } from "../lib/toast";

const TEXTS_PER_PAGE = 5;

const BUILTIN_LOCALES = [
  { code: "en", labelKey: "extensionTexts.locales.en" },
  { code: "de", labelKey: "extensionTexts.locales.de" },
  { code: "fr", labelKey: "extensionTexts.locales.fr" },
  { code: "es", labelKey: "extensionTexts.locales.es" },
  { code: "it", labelKey: "extensionTexts.locales.it" }
];
const BUILTIN_CODES = new Set(BUILTIN_LOCALES.map((locale) => locale.code));

function normalizeTexts(raw = {}) {
  const normalized = {};
  Object.keys(raw).forEach((code) => {
    normalized[code] = { ...(raw[code] || {}) };
    EXTENSION_TEXT_KEYS.forEach((key) => {
      if (normalized[code][key] == null) normalized[code][key] = "";
    });
  });
  return normalized;
}

function buildPageItems(page, totalPages) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    return pages;
  }

  pages.push(1);
  if (page > 3) pages.push("ellipsis-left");
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p += 1) {
    pages.push(p);
  }
  if (page < totalPages - 2) pages.push("ellipsis-right");
  pages.push(totalPages);
  return pages;
}

export function ExtensionTextsPage() {
  const { t } = useLocale();
  const [currentLocale, setCurrentLocale] = useState("en");
  const [textsByLocale, setTextsByLocale] = useState({});
  const [initialTextsByLocale, setInitialTextsByLocale] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [newLocaleCode, setNewLocaleCode] = useState("");
  const localeStorageKey = useRef("extensionTexts.selectedLocale");

  const localeCodes = Object.keys(textsByLocale).sort();
  const totalPages = Math.max(1, Math.ceil(EXTENSION_TEXT_KEYS.length / TEXTS_PER_PAGE));
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const start = (page - 1) * TEXTS_PER_PAGE;
  const keysOnPage = EXTENSION_TEXT_KEYS.slice(start, start + TEXTS_PER_PAGE);
  const isCustomLocale = currentLocale && !BUILTIN_CODES.has(currentLocale);
  const currentTexts = textsByLocale[currentLocale] || {};
  const isDirty = useMemo(
    () => isFormDirty(initialTextsByLocale, textsByLocale),
    [initialTextsByLocale, textsByLocale]
  );

  const localeLabel = useCallback(
    (code) => {
      const builtIn = BUILTIN_LOCALES.find((locale) => locale.code === code);
      return builtIn ? t(builtIn.labelKey) : code;
    },
    [t]
  );

  useEffect(() => {
    try {
      const saved = localeStorageKey.current && window?.localStorage?.getItem(localeStorageKey.current);
      const normalized = saved?.toString().trim().toLowerCase().split("-")[0];
      if (normalized) setCurrentLocale(normalized);
    } catch {
      // ignore
    }

    let cancelled = false;
    setLoading(true);

    apiFetch("/api/extension_texts")
      .then((data) => {
        if (cancelled) return;
        const normalized = normalizeTexts(data.extension_texts || {});
        setTextsByLocale(normalized);
        setInitialTextsByLocale(normalized);
        setCurrentLocale((prev) => {
          const available = Object.keys(normalized);
          if (available.length === 0) return "en";
          const serverSelected = data?.selected_locale?.toString().trim().toLowerCase().split("-")[0];
          if (serverSelected && normalized[serverSelected]) return serverSelected;
          if (prev && normalized[prev]) return prev;
          return normalized.en ? "en" : available[0];
        });
      })
      .catch(() => {
        if (!cancelled) showErrorToast(t("extensionTexts.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    try {
      if (!currentLocale) return;
      const key = localeStorageKey.current;
      if (!key || !window?.localStorage) return;
      window.localStorage.setItem(key, currentLocale);
    } catch {
      // ignore
    }
  }, [currentLocale]);

  useEffect(() => {
    setPage(1);
  }, [currentLocale]);

  const handleFieldChange = useCallback(
    (key, value) => {
      setTextsByLocale((prev) => ({
        ...prev,
        [currentLocale]: {
          ...(prev[currentLocale] || {}),
          [key]: value
        }
      }));
    },
    [currentLocale]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await apiFetch("/api/extension_texts", {
        method: "PATCH",
        body: JSON.stringify({
          extension_texts: textsByLocale,
          selected_locale: currentLocale
        })
      });
      const normalized = normalizeTexts(data.extension_texts || {});
      setTextsByLocale(normalized);
      setInitialTextsByLocale(normalized);
      showSuccessToast(t("extensionTexts.saveSuccess"));
    } catch {
      showErrorToast(t("extensionTexts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => setTextsByLocale({ ...initialTextsByLocale });

  const handleResetToDefault = async () => {
    try {
      const data = await apiFetch("/api/extension_texts/defaults");
      const defaultForLocale = data.extension_texts?.[currentLocale] || {};
      setTextsByLocale((prev) => ({
        ...prev,
        [currentLocale]: { ...defaultForLocale }
      }));
    } catch {
      showErrorToast(t("extensionTexts.loadError"));
    }
  };

  const handleAddLanguage = () => {
    const code = newLocaleCode.trim().toLowerCase().replace(/-.*/, "");
    if (!code || !/^[a-z]{2}$/.test(code)) return;
    if (textsByLocale[code]) {
      setCurrentLocale(code);
      setNewLocaleCode("");
      return;
    }
    const empty = EXTENSION_TEXT_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});
    setTextsByLocale((prev) => ({ ...prev, [code]: empty }));
    setCurrentLocale(code);
    setNewLocaleCode("");
  };

  const handleRemoveLanguage = () => {
    if (!isCustomLocale) return;
    const next = { ...textsByLocale };
    delete next[currentLocale];
    setTextsByLocale(next);
    const remaining = Object.keys(next);
    setCurrentLocale(remaining.includes("en") ? "en" : remaining[0] || "en");
  };

  if (loading) {
    return <LoadingState label={t("common.loading")} />;
  }

  return (
    <>
      <PortalFormSaveBar isDirty={isDirty} onSave={handleSave} onDiscard={handleDiscard} saving={saving} />

      <div className="portal-page-stack">
        <Surface className="portal-page-surface">
          <h2 className="portal-panel__title">{t("extensionTexts.pageTitle")}</h2>
          <p className="portal-panel__description">{t("extensionTexts.description")}</p>
          <Select
            selectedKey={currentLocale}
            onSelectionChange={(key) => setCurrentLocale(String(key))}
            className="w-full"
            placeholder={t("extensionTexts.language")}
          >
            <Label>{t("extensionTexts.language")}</Label>
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {localeCodes.map((code) => (
                  <ListBox.Item key={code} id={code} textValue={localeLabel(code)}>
                    {localeLabel(code)}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
          <div className="portal-add-language-row">
            <div className="portal-add-language-row__field">
              <PortalTextField
                label={t("extensionTexts.addLanguage")}
                placeholder={t("extensionTexts.addLanguagePlaceholder")}
                value={newLocaleCode}
                onChange={setNewLocaleCode}
              />
            </div>
            <div className="portal-field portal-add-language-row__action">
              <span className="portal-field__label portal-add-language-row__label-spacer" aria-hidden="true">
                {t("extensionTexts.addLanguage")}
              </span>
              <Button
                variant="secondary"
                className="h-12 min-h-12 w-full"
                onPress={handleAddLanguage}
                isDisabled={!newLocaleCode.trim()}
              >
                {t("extensionTexts.addLanguage")}
              </Button>
            </div>
          </div>
          {!isCustomLocale ? (
            <Button variant="secondary" onPress={handleResetToDefault}>
              {t("extensionTexts.resetToDefault")}
            </Button>
          ) : (
            <Button variant="ghost" className="text-red-800" onPress={handleRemoveLanguage}>
              {t("extensionTexts.removeLanguage")}
            </Button>
          )}
        </Surface>

        <Surface className="portal-page-surface">
          <h3 className="portal-panel__section-title">{t("extensionTexts.preview.title")}</h3>
          <p className="portal-panel__description">{t("extensionTexts.preview.description")}</p>
          <WiderrufFormPreview texts={currentTexts} localeLabel={localeLabel(currentLocale)} />
        </Surface>

        <Surface className="portal-page-surface">
          <h3 className="portal-panel__section-title">{t("extensionTexts.textsHeading")}</h3>
          {keysOnPage.map((key) => (
            <PortalTextField
              key={key}
              label={t(`extensionTexts.keys.${key}`)}
              value={currentTexts[key] ?? ""}
              onChange={(v) => handleFieldChange(key, v)}
            />
          ))}
          <Pagination className="portal-pagination">
            <Pagination.Summary className="portal-pagination__summary">
              {t("extensionTexts.page")} {page} / {totalPages}
            </Pagination.Summary>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={page <= 1}
                  onPress={() => setPage((value) => Math.max(1, value - 1))}
                >
                  <Pagination.PreviousIcon />
                  <span>{t("extensionTexts.previous")}</span>
                </Pagination.Previous>
              </Pagination.Item>
              {pageItems.map((item) =>
                typeof item === "number" ? (
                  <Pagination.Item key={item}>
                    <Pagination.Link isActive={item === page} onPress={() => setPage(item)}>
                      {item}
                    </Pagination.Link>
                  </Pagination.Item>
                ) : (
                  <Pagination.Item key={item}>
                    <Pagination.Ellipsis />
                  </Pagination.Item>
                )
              )}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={page >= totalPages}
                  onPress={() => setPage((value) => Math.min(totalPages, value + 1))}
                >
                  <span>{t("extensionTexts.next")}</span>
                  <Pagination.NextIcon />
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </Surface>
      </div>
    </>
  );
}
